import { type IconProps } from "@chakra-ui/react";
import {
  AmazonRedshiftIcon,
  BigQueryIcon,
  DatabricksIcon,
  SnowflakeIcon,
  SalesforceIcon,
  HubspotIcon,
  GoogleAdsIcon,
  FacebookIcon,
  IterableIcon,
  AzureIcon,
} from "ui/icons/integrations";
import {
  ReverseEtlIcon,
  CustomerStudioIcon,
  PersonalizationApiIcon,
  MatchBoosterIcon,
  EventCollectionIcon,
  IdentityResolutionIcon,
  CampaignIntelligenceIcon,
  AiDecisioningIcon,
  ReinforcementLearningIcon,
  InsightsIcon,
} from "ui/icons";
import { DbtIcon, FivetranIcon, SigmaIcon, LookerIcon } from "ui/icons/extensions";
import { type LinkProps } from "ui/link";
import { type StaticImageData } from "next/image";
import { type BrandColor } from "contexts/color-theme";
import { integrationsCount } from "../../constants/integrations";

export type NavTabProps = PlatformTabProps | StandardTabProps;

type BaseTabProps = {
  type: "platform" | "standard";
  label: string;
  href?: string;
  columns?: NavColumnProps[];
};

export type PlatformTabProps = BaseTabProps & {
  type: "platform";
  teaser: PlatformTeaserProps;
};

export type StandardTabProps = BaseTabProps & {
  type: "standard";
  teaser?: ImageTeaserProps;
};

export type ImageTeaserProps = {
  label: string;
  subLabel: string;
  image: string;
  href: string;
  ctaText?: string;
};

export type PlatformTeaserProps = {
  label: string;
  items: SubNavItemProps[];
};

export type NavColumnProps = {
  label: string;
  subLabel?: string;
  primaryItem?: SubNavItemProps;
  items?: SubNavItemProps[];
};

export type SubNavItemProps = {
  label: string;
  subLabel?: string;
  href: string;
  target?: LinkProps["target"];
  tag?: NavTagProps;
  isArrowLink?: boolean;
  icon?: (props: IconProps) => JSX.Element;
  image?: StaticImageData;
};

export type NavTagProps = {
  text: string;
  color: BrandColor;
};

export const navTabs: NavTabProps[] = [
  {
    type: "platform",
    label: "Platform",
    columns: [
      {
        label: "Composable CDP",
        primaryItem: {
          label: "Composable CDP",
          href: "/platform/composable-cdp",
        },
        items: [
          {
            label: "Customer Studio",
            subLabel: "Build audiences",
            href: "/platform/customer-studio",
            icon: CustomerStudioIcon,
          },
          {
            label: "Reverse ETL",
            subLabel: `Sync data`,
            href: "/platform/reverse-etl",
            icon: ReverseEtlIcon,
          },
          {
            label: "Identity Resolution",
            subLabel: "Stitch users together",
            href: "/platform/identity-resolution",
            icon: IdentityResolutionIcon,
          },
          {
            label: "Hightouch Events",
            subLabel: "Collect data",
            href: "/platform/events",
            icon: EventCollectionIcon,
          },
          {
            label: "Real-time Personalization",
            subLabel: "Dynamic digital experiences",
            href: "/platform/real-time-personalization",
            icon: PersonalizationApiIcon,
          },
          {
            label: "Match Booster",
            subLabel: "Increase ad match rates",
            href: "/platform/match-booster",
            icon: MatchBoosterIcon,
          },
          {
            label: "Campaign Intelligence",
            subLabel: "Analyze campaigns",
            href: "/platform/campaign-intelligence",
            icon: CampaignIntelligenceIcon,
          },
        ],
      },
      {
        label: "AI Decisioning",
        primaryItem: {
          label: "AI Decisioning",
          href: "/platform/ai-decisioning",
        },
        items: [
          {
            label: "AI agents",
            subLabel: "Deliver 1:1 experiences at scale",
            href: "/platform/ai-decisioning#ai-agents",
            icon: AiDecisioningIcon,
          },
          {
            label: "Reinforcement learning",
            subLabel: "Continuous optimization",
            href: "/blog/rl-agents-for-marketing",
            icon: ReinforcementLearningIcon,
          },
          {
            label: "Insights",
            subLabel: "Uncover new opportunities",
            href: "/platform/ai-decisioning#insights",
            icon: InsightsIcon,
          },
        ],
      },
    ],
    teaser: {
      label: "Enterprise-ready platform features",
      items: [
        {
          label: "Enterprise-level security",
          subLabel: "Enterprise-level security with SOC 2 Type 2 and ISO 27001 certification",
          href: "/platform/security",
        },
        {
          label: "Observability",
          subLabel: "Full transparency and control to monitor and troubleshoot",
          href: "/platform/observability",
        },
        {
          label: "Governance & access control",
          subLabel: "Robust access control and change management",
          href: "/docs/workspace-management/overview",
          target: "_blank",
        },
      ],
    },
  },
  {
    type: "standard",
    label: "Solutions",
    columns: [
      {
        label: "By team",
        items: [
          {
            label: "Marketing",
            href: "/solutions/marketing",
          },
          {
            label: "Advertising",
            href: "/solutions/advertising",
          },
          {
            label: "Data",
            href: "/solutions/data",
          },
          {
            label: "Operations",
            href: "/solutions/operations",
          },
        ],
      },
      {
        label: "By industry",
        items: [
          {
            label: "Retail & eCommerce",
            href: "/solutions/retail",
          },
          {
            label: "Media & Entertainment",
            href: "/solutions/media",
          },
          {
            label: "Financial Services",
            href: "/solutions/financial-services",
          },
          {
            label: "Healthcare",
            href: "/solutions/healthcare",
          },
          {
            label: "B2B SaaS",
            href: "/solutions/saas",
          },
        ],
      },
      {
        label: "For marketing",
        items: [
          {
            label: "Audience segmentation",
            href: "/platform/customer-studio",
          },
          {
            label: "Journey orchestration",
            href: "/platform/customer-studio#journeys",
          },
          {
            label: "Real-time personalization",
            href: "/platform/real-time-personalization",
          },
          {
            label: "Profile enrichment",
            href: "/platform/customer-studio#traits",
          },
          {
            label: "Customer 360",
            href: "/platform/identity-resolution",
          },
        ],
      },
      {
        label: "For advertising",
        items: [
          {
            label: "Audience targeting",
            href: "/playbooks/improve-conversions-with-personalized-funnel-based-ad-retargeting",
          },
          {
            label: "Audience suppression",
            href: "/playbooks/Suppress-Existing-Customers-from-Facebook-Campaigns",
          },
          {
            label: "Conversion tracking",
            href: "/playbooks/send-better-signals-with-facebook-conversion-api",
          },
          {
            label: "Match rate boosting",
            href: "/platform/match-booster",
          },
        ],
      },
    ],
    teaser: {
      label: "Featured",
      subLabel: "Learn about the benefits of a Composable CDP and how it compares to a traditional CDP solution",
      image: "https://cdn.sanity.io/images/pwmfmi47/production/37110b9452fff8098d4a8e4c67b5b8e9889f7e6a-2400x1254.jpg",
      href: "/resources/traditional-cdp-vs-composable-cdp",
    },
  },
  {
    type: "standard",
    label: "Integrations",
    columns: [
      {
        label: "All integrations",
        items: [
          {
            label: "Integration catalog",
            subLabel: `Hightouch integrates with ${integrationsCount}+ tools spanning Advertising, Marketing Automation, CRMs, Customer Success, and more`,
            href: "/integrations",
          },
          {
            label: `See all ${integrationsCount}+ integrations`,
            href: "/integrations",
            isArrowLink: true,
          },
        ],
      },
      {
        label: "Popular sources",
        items: [
          {
            label: "Snowflake",
            href: "/solutions/snowflake-cdp",
            icon: SnowflakeIcon,
          },
          {
            label: "Databricks",
            href: "/solutions/databricks-cdp",
            icon: DatabricksIcon,
          },
          {
            label: "Google BigQuery",
            href: "/solutions/google-bigquery-cdp",
            icon: BigQueryIcon,
          },
          {
            label: "AWS Redshift",
            href: "/solutions/aws-redshift-cdp",
            icon: AmazonRedshiftIcon,
          },
          {
            label: "Azure Synapse",
            href: "/solutions/azure-synapse-cdp",
            icon: AzureIcon,
          },
          {
            label: "See all sources",
            href: "/integrations?type=source",
            isArrowLink: true,
          },
        ],
      },
      {
        label: "Popular destinations",
        items: [
          {
            label: "Salesforce",
            href: "/integrations/destinations/salesforce",
            icon: SalesforceIcon,
          },
          {
            label: "HubSpot",
            href: "/integrations/destinations/hubspot",
            icon: HubspotIcon,
          },
          {
            label: "Google Ads",
            href: "/integrations/destinations/google",
            icon: GoogleAdsIcon,
          },
          {
            label: "Facebook Ads",
            href: "/integrations/destinations/facebook",
            icon: FacebookIcon,
          },
          {
            label: "Iterable",
            href: "/integrations/destinations/iterable",
            icon: IterableIcon,
          },
          {
            label: "See all destinations",
            href: "/integrations?type=destination",
            isArrowLink: true,
          },
        ],
      },
      {
        label: "Popular extensions",
        items: [
          {
            label: "dbt",
            href: "/solutions/dbt",
            icon: DbtIcon,
          },
          {
            label: "Sigma",
            href: "/solutions/sigma",
            icon: SigmaIcon,
          },
          {
            label: "Looker",
            href: "/docs/extensions/looker-models",
            target: "_blank",
            icon: LookerIcon,
          },
          {
            label: "Fivetran",
            href: "/solutions/fivetran",
            icon: FivetranIcon,
          },
        ],
      },
    ],
  },
  {
    type: "standard",
    label: "Customers",
    href: "/customers",
  },
  {
    type: "standard",
    label: "Resources",
    columns: [
      {
        label: "Explore",
        items: [
          { label: "Blog", href: "/blog/category/all/page/1" },
          { label: "Learn", href: "/learn" },
          { label: "Whitepapers & guides", href: "/resources" },
          { label: "Playbooks", href: "/playbooks" },
          {
            label: "Data Activators",
            href: "https://www.youtube.com/channel/UCTiIAJWiX6KXjGyd6umKl8w",
            target: "_blank",
          },
          { label: "CDP Comparisons", href: "/compare-cdps" },
        ],
      },
      {
        label: "Documentation",
        items: [
          {
            label: "Core concepts",
            subLabel: "Understand the basics of how Hightouch works",
            href: "/docs/getting-started/concepts",
            target: "_blank",
          },
          {
            label: "Syncs",
            subLabel: "Syncs define how you want to get data from your source to destination",
            href: "/docs/syncs/overview",
            target: "_blank",
          },
          {
            label: "Models",
            subLabel: "Hightouch models define and organize data from a source",
            href: "/docs/models/creating-models",
            target: "_blank",
          },
          {
            label: "Read our docs",
            href: "/docs",
            target: "_blank",
            isArrowLink: true,
          },
        ],
      },
      {
        label: "Get started",
        items: [
          {
            label: "Talk to us",
            subLabel: "Schedule 30-minutes to talk through how to solve your use cases",
            href: "/demo",
          },
          {
            label: "Events",
            subLabel: "Find conferences, local events, and webinars to learn more",
            href: "/events",
          },
          {
            label: "Solutions partners",
            subLabel: "Our Partners design, build and deliver exceptional data solutions",
            href: "/solutions-partners",
          },
        ],
      },
    ],
    teaser: {
      label: "Featured",
      subLabel: "The complete guide to Composable Customer Data Platforms",
      href: "/resources/composable-cdp-ebook",
      ctaText: "Download the whitepaper",
      image: "https://cdn.sanity.io/images/pwmfmi47/production/d857fa88789810b4010963255026020f0a57482b-548x287.png",
    },
  },
  {
    type: "standard",
    label: "Pricing",
    href: "/pricing",
  },
];
